import React, { useEffect, useState, useRef } from "react";
import { PenLine, Trash2, Eye, EyeOff, Pencil, X, ExternalLink, Upload } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const DEFAULT_IMAGES = ["/blog/couple-diaspora.jpg", "/blog/mariage-africain.jpg", "/blog/amitie-africaine.jpg"];
const TAGS = ["Conseils rencontre", "Mariage", "Amitié", "Diaspora"];

const textToSections = (text) => {
  const sections = [];
  let cur = { h2: null, paras: [] };
  for (const block of (text || "").split(/\n\s*\n/)) {
    const b = block.trim();
    if (!b) continue;
    if (b.startsWith("## ")) {
      const lines = b.split("\n");
      if (cur.h2 || cur.paras.length) sections.push(cur);
      cur = { h2: lines[0].slice(3).trim(), paras: lines.slice(1).map((l) => l.trim()).filter(Boolean) };
    } else {
      cur.paras.push(...b.split("\n").map((l) => l.trim()).filter(Boolean));
    }
  }
  if (cur.h2 || cur.paras.length) sections.push(cur);
  return sections;
};

const sectionsToText = (sections) =>
  (sections || []).map((s) => (s.h2 ? `## ${s.h2}\n\n` : "") + (s.paras || []).join("\n\n")).join("\n\n");

const emptyForm = { post_id: null, title: "", tag: TAGS[0], description: "", image: DEFAULT_IMAGES[0], imageAlt: "", content: "", published: false };

export const AdminBlog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/blog");
      setPosts(data.articles || []);
    } catch (e) {
      toast.error("Impossible de charger les articles.");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const uploadImage = async (file) => {
    if (!file || uploading) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/blog/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image: data.image }));
      toast.success("Photo importée ! 📷");
    } catch (e) {
      toast.error(e.response?.data?.detail || "L'envoi de la photo a échoué.");
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const save = async (publish) => {
    if (saving) return;
    if (!form.title.trim()) { toast.error("Le titre est requis."); return; }
    if (!form.content.trim()) { toast.error("Le contenu est vide."); return; }
    setSaving(true);
    const payload = {
      title: form.title, description: form.description, tag: form.tag,
      image: form.image, image_alt: form.imageAlt,
      sections: textToSections(form.content), published: publish,
    };
    try {
      if (form.post_id) await api.put(`/admin/blog/${form.post_id}`, payload);
      else await api.post("/admin/blog", payload);
      toast.success(publish ? "Article publié ! Il est visible sur le blog. 🎉" : "Brouillon enregistré.");
      setForm(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Enregistrement impossible.");
    }
    setSaving(false);
  };

  const togglePublish = async (p) => {
    try {
      await api.put(`/admin/blog/${p.post_id}`, {
        title: p.title, description: p.description, tag: p.tag,
        image: p.image, image_alt: p.imageAlt, sections: p.sections,
        published: !p.published,
      });
      toast.success(!p.published ? "Article publié ✔" : "Article dépublié (brouillon).");
      load();
    } catch (e) {
      toast.error("Action impossible.");
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/blog/${confirmDelete.post_id}`);
      toast.success("Article supprimé.");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error("Suppression impossible.");
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-5" data-testid="admin-blog">
      <div className="bg-white rounded-3xl border border-kora-line p-5 mb-5">
        <p className="font-display font-black text-kora-ink flex items-center gap-2 mb-1">
          <PenLine size={18} className="text-kora-terracotta" /> Rédiger un article
        </p>
        <p className="text-xs text-kora-muted mb-3">Rédigez votre article (par exemple avec ChatGPT ou Claude), collez-le ici, importez votre propre photo de couverture, puis publiez quand vous êtes prêt.</p>
        <button
          data-testid="admin-blog-new-btn"
          onClick={() => setForm({ ...emptyForm, image: DEFAULT_IMAGES[posts.length % DEFAULT_IMAGES.length] })}
          className="inline-flex items-center gap-2 bg-kora-terracotta text-white font-bold text-sm px-5 py-3 rounded-full"
        >
          <PenLine size={16} /> Écrire un nouvel article
        </button>
      </div>

      {loading ? (
        <div className="py-14 flex justify-center">
          <div className="w-10 h-10 border-4 border-kora-terracotta border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-2" data-testid="admin-blog-list">
          {posts.length === 0 && <p className="text-center text-kora-muted text-sm py-8">Aucun article en base. Les 3 articles historiques du blog restent visibles sur le site.</p>}
          {posts.map((p) => (
            <div key={p.post_id} data-testid={`admin-blog-post-${p.post_id}`} className="bg-white rounded-2xl border border-kora-line p-3 flex items-center gap-3">
              <img src={p.image} alt="" className="w-14 h-14 rounded-xl object-cover bg-kora-sand shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <p className="font-bold text-sm text-kora-ink truncate">{p.title}</p>
                  {p.published ? (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-green-100 text-green-700">PUBLIÉ</span>
                  ) : (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-kora-ochre/20 text-[#C77826]">BROUILLON</span>
                  )}
                </div>
                <p className="text-xs text-kora-muted truncate">{p.tag} · {p.dateFr} · {p.readTime}</p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {p.published && (
                  <a data-testid={`admin-blog-view-${p.post_id}`} href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" title="Voir sur le blog" className="w-9 h-9 rounded-full bg-kora-sand text-kora-ink flex items-center justify-center hover:bg-kora-ink hover:text-white transition-colors">
                    <ExternalLink size={15} />
                  </a>
                )}
                <button
                  data-testid={`admin-blog-edit-${p.post_id}`}
                  title="Modifier"
                  onClick={() => setForm({ post_id: p.post_id, title: p.title, tag: p.tag, description: p.description, image: p.image, imageAlt: p.imageAlt, content: sectionsToText(p.sections), published: p.published })}
                  className="w-9 h-9 rounded-full bg-kora-sand text-kora-ink flex items-center justify-center hover:bg-kora-ink hover:text-white transition-colors"
                >
                  <Pencil size={15} />
                </button>
                <button
                  data-testid={`admin-blog-toggle-${p.post_id}`}
                  title={p.published ? "Dépublier" : "Publier"}
                  onClick={() => togglePublish(p)}
                  className="w-9 h-9 rounded-full bg-green-50 text-green-600 flex items-center justify-center hover:bg-green-600 hover:text-white transition-colors"
                >
                  {p.published ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
                <button
                  data-testid={`admin-blog-delete-${p.post_id}`}
                  title="Supprimer"
                  onClick={() => setConfirmDelete(p)}
                  className="w-9 h-9 rounded-full bg-red-50 text-red-500 flex items-center justify-center hover:bg-red-500 hover:text-white transition-colors"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {form && (
        <div className="fixed inset-0 z-[85] bg-kora-ink/70 backdrop-blur-sm flex items-end md:items-center justify-center p-4" data-testid="admin-blog-editor" onClick={() => setForm(null)}>
          <div className="bg-white w-full md:max-w-2xl rounded-3xl p-6 animate-float-up max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-black text-lg text-kora-ink">{form.post_id ? "Modifier l'article" : "Nouvel article"}</h3>
              <button data-testid="admin-blog-editor-close" onClick={() => setForm(null)} className="text-kora-muted hover:text-kora-ink"><X size={20} /></button>
            </div>
            <input
              data-testid="admin-blog-title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Titre de l'article"
              className="w-full px-4 py-3 rounded-2xl border border-kora-line focus:outline-none focus:border-kora-terracotta text-sm font-bold mb-3"
            />
            <div className="flex gap-2 mb-3">
              <select
                data-testid="admin-blog-tag"
                value={form.tag}
                onChange={(e) => setForm({ ...form, tag: e.target.value })}
                className="px-4 py-3 rounded-2xl border border-kora-line focus:outline-none focus:border-kora-terracotta text-sm bg-white"
              >
                {TAGS.map((t) => <option key={t}>{t}</option>)}
              </select>
              <input
                data-testid="admin-blog-image"
                value={form.image}
                onChange={(e) => setForm({ ...form, image: e.target.value })}
                placeholder="URL de l'image (https://… ou /blog/…)"
                className="flex-1 px-4 py-3 rounded-2xl border border-kora-line focus:outline-none focus:border-kora-terracotta text-sm"
              />
            </div>
            <div className="flex gap-2 mb-3 items-center">
              <img data-testid="admin-blog-image-preview" src={form.image} alt="" className="w-24 h-16 rounded-xl object-cover bg-kora-sand shrink-0 border border-kora-line" />
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                data-testid="admin-blog-upload-input"
                onChange={(e) => uploadImage(e.target.files?.[0])}
              />
              <button
                data-testid="admin-blog-upload-btn"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-kora-ink text-white text-xs font-bold disabled:opacity-60"
              >
                {uploading ? <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Upload size={13} />}
                {uploading ? "Envoi de la photo…" : "Importer ma photo"}
              </button>
            </div>
            <div className="flex gap-2 mb-3">
              {DEFAULT_IMAGES.map((img) => (
                <button key={img} data-testid={`admin-blog-img-${img.split("/").pop()}`} onClick={() => setForm({ ...form, image: img })} className={`rounded-xl overflow-hidden border-2 ${form.image === img ? "border-kora-terracotta" : "border-transparent"}`}>
                  <img src={img} alt="" className="w-16 h-12 object-cover" />
                </button>
              ))}
            </div>
            <textarea
              data-testid="admin-blog-description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Description courte (affichée sur la liste du blog et sur Google, ~160 caractères)"
              rows={2}
              className="w-full px-4 py-3 rounded-2xl border border-kora-line focus:outline-none focus:border-kora-terracotta text-sm mb-3 resize-none"
            />
            <p className="text-[11px] text-kora-muted mb-1">Contenu — commencez une ligne par « ## » pour un sous-titre, séparez les paragraphes par une ligne vide.</p>
            <textarea
              data-testid="admin-blog-content"
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              placeholder={"Introduction de l'article…\n\n## Premier sous-titre\n\nParagraphe…"}
              rows={14}
              className="w-full px-4 py-3 rounded-2xl border border-kora-line focus:outline-none focus:border-kora-terracotta text-sm mb-4 leading-relaxed"
            />
            <div className="flex gap-2">
              <button data-testid="admin-blog-save-draft" onClick={() => save(false)} disabled={saving} className="flex-1 py-3 rounded-full border border-kora-line font-semibold text-kora-ink text-sm disabled:opacity-60">
                Enregistrer le brouillon
              </button>
              <button data-testid="admin-blog-publish" onClick={() => save(true)} disabled={saving} className="flex-1 py-3 rounded-full bg-kora-terracotta text-white font-bold text-sm disabled:opacity-60">
                {saving ? "Enregistrement…" : "Publier sur le blog"}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-[90] bg-kora-ink/70 backdrop-blur-sm flex items-end md:items-center justify-center p-4" data-testid="admin-blog-delete-modal" onClick={() => setConfirmDelete(null)}>
          <div className="bg-white w-full md:max-w-sm rounded-3xl p-6 animate-float-up text-center" onClick={(e) => e.stopPropagation()}>
            <div className="w-14 h-14 rounded-full bg-red-50 text-red-500 flex items-center justify-center mx-auto mb-3"><Trash2 size={26} /></div>
            <h3 className="font-display font-black text-lg text-kora-ink">Supprimer cet article ?</h3>
            <p className="text-sm text-kora-muted mt-1 mb-5 line-clamp-2">« {confirmDelete.title} » sera définitivement supprimé du blog.</p>
            <div className="flex gap-2">
              <button data-testid="admin-blog-delete-cancel" onClick={() => setConfirmDelete(null)} className="flex-1 py-2.5 rounded-full border border-kora-line font-semibold text-kora-ink">Annuler</button>
              <button data-testid="admin-blog-delete-confirm" onClick={doDelete} className="flex-1 py-2.5 rounded-full bg-red-500 text-white font-bold">Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBlog;
